import { z } from 'zod/v4';

/**
 * Button-adjacent setting bags shared by several widgets (button, form,
 * payment-form, …). Originally authored for the button widget's item schema
 * and promoted here when the form widget needed them; the button schemas
 * re-export these for backwards compatibility.
 */

/**
 * Visual style preset, mirroring `EButtonStyleType` from the host app.
 * `'custom'` switches the editor to the per-button colour controls.
 */
export const buttonStyleTypeSchema = z
    .enum(['classic', 'primary', 'secondary', 'custom'])
    .describe(
        "Visual style preset. 'primary' / 'secondary' / 'classic' render from a fixed site theme and ignore " +
            "buttonBase/buttonHover/advancedBorderSettings/advancedRadius entirely; 'custom' unlocks those fields."
    );

/**
 * Sizing strategy, mirroring `EButtonWidthType`. Old persisted buttons may
 * omit it entirely — the styles renderers fall back to `'auto'`.
 */
export const buttonWidthTypeSchema = z.enum(['auto', 'full', 'fixed']).describe('Sizing strategy.');

/**
 * Button size bucket, mirroring `ESize`.
 */
export const buttonSizeSchema = z.enum(['S', 'M', 'L']).describe('Button size bucket.');

/**
 * Horizontal alignment bucket, mirroring `EHorizontalPosition`.
 */
export const buttonHorizontalPositionSchema = z.enum(['L', 'M', 'R']).describe('Horizontal alignment bucket.');

/**
 * Inline icon settings, mirroring `ButtonIcon` from
 * `libs/components/sp-button-icon`. `icon` holds a raw SVG template string —
 * the host app sources defaults from `buttonIconTemplates`, which this
 * package deliberately doesn't depend on (callers seed the default icon).
 */
export const buttonIconSchema = z
    .object({
        isActive: z.boolean(),
        icon: z.string(),
        position: buttonHorizontalPositionSchema,
    })
    .describe('Inline icon settings.');

/**
 * Hover/static animation effects, mirroring `ButtonAdditionalEffectsSettings`
 * from `libs/components/sp-button-additional-effects-settings`.
 */
export const buttonAdditionalEffectsSchema = z
    .object({
        isActive: z.boolean(),
        effectCondition: z.enum(['hover', 'static']),
        hoverEffects: z.enum(['decrease', 'increase', 'bounce', 'shake']),
        staticEffects: z.enum(['twinkle', 'shine']),
    })
    .describe('Hover/static animation effect settings.');

/**
 * Per-corner radius overrides, mirroring `AdvancedRadiusSettings` from
 * `libs/components/sp-advanced-radius-settings`. Leaves are nullable —
 * the host form factories hold nulls until the user touches a corner.
 */
export const advancedRadiusSchema = z
    .object({
        borderTopLeftRadius: z.number().nullable(),
        borderTopRightRadius: z.number().nullable(),
        borderBottomLeftRadius: z.number().nullable(),
        borderBottomRightRadius: z.number().nullable(),
    })
    .describe("Per-corner border-radius overrides — only rendered when styleType is 'custom'.");

/**
 * Per-side border overrides, mirroring `AdvancedBorderSettings` from
 * `libs/components/sp-advanced-border-settings`. The whole bag defaults to
 * `null` (a single FormControl in the host forms), so consumers treat
 * absence and `null` identically. Border styles hold `ELineStyle` values,
 * kept as `z.string()` here.
 */
export const advancedBorderSettingsSchema = z
    .object({
        isActive: z.boolean(),
        isIndependentBorders: z.boolean(),
        borderTopWidth: z.number(),
        borderTopColor: z.string(),
        borderTopStyle: z.string(),
        borderRightWidth: z.number(),
        borderRightColor: z.string(),
        borderRightStyle: z.string(),
        borderBottomWidth: z.number(),
        borderBottomColor: z.string(),
        borderBottomStyle: z.string(),
        borderLeftWidth: z.number(),
        borderLeftColor: z.string(),
        borderLeftStyle: z.string(),
    })
    .describe("Per-side border overrides — only rendered when styleType is 'custom'.");

export type ButtonIconSettings = z.infer<typeof buttonIconSchema>;
export type ButtonAdditionalEffects = z.infer<typeof buttonAdditionalEffectsSchema>;
export type AdvancedRadius = z.infer<typeof advancedRadiusSchema>;
export type AdvancedBorder = z.infer<typeof advancedBorderSettingsSchema>;
